/**
 * Backend envelope: `{ success, statusCode, data, message, pagination?, error? }`.
 * Types + `unwrap`; the error helpers below are the shared ones, re-exported
 * under their gateway names.
 */

export interface ApiSuccess<T> {
  success: true;
  statusCode: number;
  data: T;
  message?: string;
  pagination?: {
    page: number;
    per_page: number;
    total: number;
    total_pages: number;
  };
}

export interface ApiFailure {
  success: false;
  statusCode: number;
  message: string;
  error: { code: string; details?: unknown };
}

export type ApiResponse<T> = ApiSuccess<T> | ApiFailure;

/** Extract `data` from a wrapped response. Throws if the response is an error envelope. */
export function unwrap<T>(payload: ApiResponse<T>): T {
  if (!payload.success) {
    const err = new Error(payload.message);
    (err as Error & { code?: string }).code = payload.error.code;
    (err as Error & { details?: unknown }).details = payload.error.details;
    throw err;
  }
  return payload.data;
}

/**
 * Error normalization + user-facing copy come from the ONE monorepo module
 * (`@clarix/utils`), which reads both envelope shapes and RTK Query rejections.
 * `NormalizedApiError` / `normalizeError` keep their gateway names so the auth,
 * invite and onboarding pages need no edit.
 */
export type { NormalizedApiError } from '@clarix/utils';
export {
  normalizeApiError as normalizeError,
  getApiErrorMessage,
  getApiErrorCode,
  getApiErrorStatus,
  getApiErrorDetails,
  isUserFriendlyMessage,
  API_ERROR_MESSAGES,
} from '@clarix/utils';
